import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MessageSquare, Send, Plus, Bot, User, Loader2, Sparkles, Copy } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';
import { isUnread, markRead } from '@/lib/chat-read-state';

interface Conversation {
  id: string;
  title: string;
  last_message_preview: string | null;
  last_message_at: string | null;
  created_at: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  metadata?: { task_id?: string } | null;
}

const SUGGESTED_PROMPTS = [
  'Check inventory levels for low-stock items',
  'Why did the last order fail?',
  'Summarize today\'s activity',
  'Review current pricing margins',
  'Check supplier order statuses',
];

export default function AgentChat() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const addToast = useUIStore((s) => s.addToast);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(routeId ?? null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [convsLoading, setConvsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [creatingConv, setCreatingConv] = useState(false);

  const lastMessageIdRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Load conversations
  useEffect(() => {
    const load = async () => {
      try {
        setConvsLoading(true);
        const res = await api.get<{ conversations: Conversation[] }>('/admin/agent/conversations');
        setConversations(res.conversations);
        if (!activeId && res.conversations.length > 0) {
          const first = res.conversations[0];
          setActiveId(first.id);
          navigate(`/admin/agent/chat/${first.id}`, { replace: true });
        }
      } catch (err) {
        addToast(err instanceof Error ? err.message : 'Failed to load conversations', 'error');
      } finally {
        setConvsLoading(false);
      }
    };
    load();
  }, []);

  // Load messages when active conversation changes
  useEffect(() => {
    if (!activeId) return;
    setMessages([]);
    lastMessageIdRef.current = null;
    const load = async () => {
      try {
        setMessagesLoading(true);
        const res = await api.get<{ messages: Message[] }>(`/admin/agent/conversations/${activeId}/messages`);
        setMessages(res.messages);
        if (res.messages.length > 0) {
          lastMessageIdRef.current = res.messages[res.messages.length - 1].id;
        }
      } catch (err) {
        addToast(err instanceof Error ? err.message : 'Failed to load messages', 'error');
      } finally {
        setMessagesLoading(false);
      }
    };
    load();
  }, [activeId]);

  // Poll for new messages every 3 seconds (with 60s timeout for thinking state)
  const thinkingStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (thinking) {
      thinkingStartRef.current = Date.now();
    } else {
      thinkingStartRef.current = null;
    }
  }, [thinking]);

  const pollMessages = useCallback(async () => {
    if (!activeId) return;
    try {
      const after = lastMessageIdRef.current ?? '';
      const res = await api.get<{ messages: Message[] }>(
        `/admin/agent/conversations/${activeId}/messages${after ? `?after=${after}` : ''}`
      );
      if (res.messages.length > 0) {
        setMessages((prev) => [...prev, ...res.messages]);
        lastMessageIdRef.current = res.messages[res.messages.length - 1].id;
        setThinking(false);
      } else if (thinking && thinkingStartRef.current && Date.now() - thinkingStartRef.current > 60_000) {
        // Timeout: no response after 60 seconds
        setThinking(false);
        addToast('Agent response timed out. Please try again.', 'error');
      }
    } catch {
      // Silently ignore poll errors
    }
  }, [activeId, thinking, addToast]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!activeId) return;
    pollRef.current = setInterval(pollMessages, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeId, pollMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const handleNewConversation = async () => {
    try {
      setCreatingConv(true);
      const res = await api.post<{ conversation: Conversation }>('/admin/agent/conversations', {
        title: `Conversation ${new Date().toLocaleDateString()}`,
      });
      setConversations((prev) => [res.conversation, ...prev]);
      setActiveId(res.conversation.id);
      setMessages([]);
      lastMessageIdRef.current = null;
      navigate(`/admin/agent/chat/${res.conversation.id}`);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to create conversation', 'error');
    } finally {
      setCreatingConv(false);
    }
  };

  const handleSelectConversation = (conv: Conversation) => {
    setActiveId(conv.id);
    markRead(conv.id);
    // Force re-render by mutating conversations array (changes localStorage-derived isUnread)
    setConversations((prev) => [...prev]);
    navigate(`/admin/agent/chat/${conv.id}`);
  };

  const handleSend = async (overrideContent?: string) => {
    const text = overrideContent ?? input;
    if (!text.trim() || !activeId || sending) return;
    const content = text.trim();
    setInput('');
    setSending(true);
    setThinking(true);

    // Optimistically add user message
    const optimistic: Message = {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const res = await api.post<{ message: Message }>(
        `/admin/agent/conversations/${activeId}/messages`,
        { content }
      );
      // Replace optimistic with real message
      setMessages((prev) =>
        prev.map((m) => (m.id === optimistic.id ? res.message : m))
      );
      lastMessageIdRef.current = res.message.id;
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to send message', 'error');
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setThinking(false);
    } finally {
      setSending(false);
    }
  };

  const handleCopyConversation = async () => {
    if (messages.length === 0) return;
    const text = messages
      .map((m) => `${m.role.toUpperCase()}: ${m.content}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      addToast('Conversation copied to clipboard', 'success');
    } catch {
      addToast('Failed to copy — clipboard unavailable', 'error');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden">
      {/* Left sidebar: conversation list */}
      <aside className="w-72 shrink-0 flex flex-col border-r border-stone-200">
        <div className="p-3 border-b border-stone-200">
          <button
            onClick={handleNewConversation}
            disabled={creatingConv}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {creatingConv ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            New Conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convsLoading ? (
            <div className="p-4 text-sm text-gray-500 text-center">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              No conversations yet
            </div>
          ) : (
            conversations.map((conv) => {
              const unread = activeId !== conv.id && isUnread(conv);
              return (
              <button
                key={conv.id}
                onClick={() => handleSelectConversation(conv)}
                className={cn(
                  'w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors',
                  activeId === conv.id ? 'bg-green-50 border-l-2 border-l-green-600' : ''
                )}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 shrink-0 text-stone-400" />
                  <span className={cn('text-sm truncate', unread ? 'font-bold text-stone-900' : 'font-medium text-stone-800')}>{conv.title}</span>
                  {unread && (
                    <span
                      data-testid={`unread-dot-${conv.id}`}
                      className="ml-auto w-2 h-2 rounded-full bg-blue-500 shrink-0"
                      aria-label="Unread"
                    />
                  )}
                </div>
                {conv.last_message_preview && (
                  <p className="text-xs text-stone-500 mt-1 truncate pl-6">
                    {conv.last_message_preview}
                  </p>
                )}
                {conv.last_message_at && (
                  <p className="text-xs text-stone-400 mt-0.5 pl-6">
                    {new Date(conv.last_message_at).toLocaleDateString()}
                  </p>
                )}
              </button>
              );
            })
          )}
        </div>
      </aside>

      {/* Right: message thread */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
          <Bot className="w-5 h-5 text-green-600" />
          <span className="font-semibold text-stone-800">
            {activeId
              ? (conversations.find((c) => c.id === activeId)?.title ?? 'Agent Chat')
              : 'Agent Chat'}
          </span>
          {activeId && messages.length > 0 && (
            <button
              onClick={handleCopyConversation}
              className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100"
              aria-label="Copy conversation"
              data-testid="copy-conversation-btn"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy all
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!activeId ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3" />
                <p>Select or create a conversation to begin</p>
              </div>
            </div>
          ) : messagesLoading ? (
            <div className="text-center text-gray-500 py-8">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <Bot className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500 mb-4">Ask your AI agent anything about your store</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTED_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => handleSend(prompt)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                      data-testid="suggested-prompt"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-gray-400" />
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
          )}

          {/* Thinking indicator */}
          {thinking && (
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-gray-600" />
              </div>
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-gray-500 italic">
                Thinking...
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div className="px-4 py-3 border-t border-stone-200">
          <div className="flex gap-2 items-end">
            <textarea
              className="flex-1 resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[40px] max-h-32"
              placeholder={activeId ? 'Message the agent… (Enter to send, Shift+Enter for newline)' : 'Select a conversation first'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!activeId || sending}
              rows={1}
            />
            <button
              onClick={() => handleSend()}
              disabled={!activeId || !input.trim() || sending}
              className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 shrink-0"
              aria-label="Send message"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-3 py-1">
          {message.content}
        </span>
      </div>
    );
  }

  const isUser = message.role === 'user';

  return (
    <div className={cn('flex items-start gap-2', isUser ? 'flex-row-reverse' : 'flex-row')}>
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center shrink-0',
          isUser ? 'bg-blue-600' : 'bg-gray-200'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-gray-600" />
        )}
      </div>
      <div
        className={cn(
          'max-w-[70%] px-4 py-2 rounded-2xl text-sm',
          isUser
            ? 'bg-blue-600 text-white rounded-tr-sm whitespace-pre-wrap'
            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
        )}
      >
        {isUser ? (
          message.content
        ) : (
          <div className="prose prose-sm prose-gray max-w-none [&>p]:mb-1 [&>ul]:mb-1 [&>ol]:mb-1 [&>pre]:bg-gray-200 [&>pre]:rounded [&>pre]:p-2">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
        {!isUser && message.metadata?.task_id && (
          <Link
            to={`/admin/agent/tasks/${message.metadata.task_id}`}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1"
          >
            View task →
          </Link>
        )}
        <div
          className={cn(
            'text-xs mt-1',
            isUser ? 'text-blue-200 text-right' : 'text-gray-400'
          )}
        >
          {new Date(message.created_at).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}
